import React, { useRef, useState, useEffect } from "react";
import { prepareImageForUpload } from "../shared/utils";
import { bm } from "./bmClient";
import { bmColors, muted } from "./bmStyles";

// The photo box at the top of a building's profile.
//
// Always drawn, whether or not there is a photo in it: the empty box is what
// invites one, and keeping both states the same size stops the page jumping
// when one arrives.
//
// A manager standing in front of the building is the right person to photograph
// it, and the right person to correct it when the wrong image goes up — so a
// filled box carries a Change photo control rather than being final.
export function BuildingPhoto({ site, isMobile, onUploaded }) {
  const fileInput = useRef(null);
  const [pending, setPending] = useState(null); // local preview while uploading
  const [error, setError] = useState("");
  const [hover, setHover] = useState(false);

  const url = site.street_view_photo_url;

  // The object URL outlives the render that made it, so it is revoked when the
  // preview is replaced or the component goes away.
  useEffect(() => () => pending && URL.revokeObjectURL(pending), [pending]);

  // A shape rather than a height: full width at a fixed pixel height meant the
  // wider the window, the more of the building `cover` threw away. Capped so a
  // large monitor does not hand one photo half the page.
  const frame = {
    width: "100%",
    aspectRatio: "16 / 9",
    maxHeight: "360px",
    borderRadius: "8px",
    display: "block",
  };

  const pick = async (e) => {
    const file = e.target.files?.[0];
    // Cleared so the same file can be picked again after a failure.
    e.target.value = "";
    if (!file) return;

    setError("");
    const preview = URL.createObjectURL(file);
    setPending(preview);

    try {
      // Resizes a 4-12MB camera original down to a few hundred KB, and turns
      // an iOS HEIC into a JPEG — Pillow rejects HEIC outright.
      const resized = await prepareImageForUpload(file);
      const body = new FormData();
      body.append("street_view_photo", resized);
      // No Content-Type header: axios sets it with the boundary for FormData,
      // and a hand-written one without a boundary is worse than none.
      const res = await bm.post(`/sites/${site.id}/photo/`, body);
      onUploaded(res.data);
      // Batched with the parent's update, so the real photo replaces the
      // preview in one paint rather than flashing the empty box between them.
      setPending(null);
    } catch (err) {
      setPending(null);
      setError(
        err.response?.data?.detail ||
          err.response?.data?.street_view_photo?.[0] ||
          "Could not upload that photo. Please try again."
      );
    }
  };

  const box = pending ? (
    <div style={{ ...frame, position: "relative" }}>
      <img
        src={pending}
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: "8px",
          display: "block",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "8px",
          backgroundColor: "rgba(255,255,255,0.75)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "14px",
          color: bmColors.text,
        }}
      >
        Uploading...
      </div>
    </div>
  ) : url ? (
    // The photo is a photo again — only the corner chip changes it, so a stray
    // click in the middle of a large image cannot open a file picker.
    <div style={{ ...frame, position: "relative" }}>
      <img
        src={url}
        alt="The building"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: "8px",
          border: `1px solid ${bmColors.border}`,
          display: "block",
        }}
      />
      {/* A generous hover zone around a small chip, so the chip is easy to
          reach without being large itself. Touch has no hover, so there the
          chip simply stays visible — otherwise a manager on a phone could
          never change a photo once one was set. */}
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: "180px",
          height: "64px",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "flex-end",
          padding: "10px",
          opacity: isMobile || hover ? 1 : 0,
          transition: "opacity 0.15s",
        }}
      >
        <button
          onClick={() => fileInput.current?.click()}
          style={{
            backgroundColor: "rgba(15,23,42,0.6)",
            color: bmColors.white,
            border: "none",
            borderRadius: "6px",
            padding: "6px 10px",
            fontSize: "12px",
            fontWeight: "600",
            cursor: "pointer",
            // Invisible until hovered, and unclickable with it — otherwise the
            // corner would swallow clicks that look like they land on the photo.
            pointerEvents: isMobile || hover ? "auto" : "none",
          }}
        >
          Change photo
        </button>
      </div>
    </div>
  ) : (
    <div
      onClick={() => fileInput.current?.click()}
      style={{
        ...frame,
        border: `1px dashed ${bmColors.border}`,
        backgroundColor: bmColors.white,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        cursor: "pointer",
        textAlign: "center",
        padding: "0 16px",
      }}
    >
      <div style={{ fontSize: "14px", color: bmColors.text }}>
        Add a photo of this building
      </div>
      <div style={muted}>Tap to take one or choose from your photos</div>
    </div>
  );

  return (
    <div style={{ marginBottom: "24px" }}>
      {box}

      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        onChange={pick}
        style={{ display: "none" }}
      />
      {error && (
        <div style={{ ...muted, color: bmColors.red, marginTop: "8px" }}>{error}</div>
      )}
    </div>
  );
}
